// @ts-strict-ignore
import React, {
  forwardRef,
  memo,
  useMemo,
  useRef,
  ReactNode,
  useCallback,
  ChangeEvent,
  useImperativeHandle,
  ForwardedRef, ComponentProps,
  JSX,
} from 'react'
import { FormValueInput, FormValueMeta } from 'types'
import { getShortName } from 'services/form'
import {
  AutocompleteGetTagProps,
  ChipProps,
  Autocomplete,
  AutocompleteInputChangeReason,
  CircularProgress,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useListBoxScrollListener } from 'hooks/search'
import TextField from '../TextField'

export interface SuggestionsFormat {
  text: string
  value: string
  hint?: string
  label?: string
}

export interface IAutoCompleteChipInputProps<T> extends Omit<
  ComponentProps<typeof Autocomplete>,
  'renderInput' | 'renderTags' | 'onChange' | 'value' | 'options' | 'getOptionLabel' | 'isOptionEqualToValue' | 'ListboxProps' | 'defaultValue'
> {
  suggestions?: T[] | ReadonlyArray<T>
  suggestionsFormat?: SuggestionsFormat
  input?: FormValueInput<T[]>
  meta?: FormValueMeta
  disabled?: boolean
  label?: ReactNode
  autoFocus?: boolean
  helperText?: ReactNode
  fullWidth?: boolean
  placeholder?: string
  value?: T[]
  name?: string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  noOptionsText?: ReactNode
  onChange?: (value: T[]) => void
  renderChip?: (chipProps: ChipProps, item: T) => ReactNode
  loadMore?: () => void
}

function areOptionsEqual<T extends { [key: string]: any }>(a: T, b: T, format: SuggestionsFormat) {
  return a[format.value] === b[format.value]
}

const useStyles = makeStyles()({
  container: {
    '& .MuiChip-root': {
      margin: '1px !important',
    },
    '& .MuiChip-label': {
      pointerEvents: 'none',
    },
  },
})

function AutoCompleteChipInput<T extends { [key: string]: any }>(props: IAutoCompleteChipInputProps<T>, forwardedRef: ForwardedRef<HTMLInputElement>) {
  const { classes } = useStyles()
  let {
    suggestions = [],
    suggestionsFormat = { text: 'name', value: 'id', hint: 'hint' },
    label,
    input,
    onChange,
    onBlur,
    meta = {} as FormValueMeta,
    value,
    placeholder,
    name,
    fullWidth,
    helperText,
    autoFocus,
    startAdornment,
    endAdornment,
    noOptionsText,
    loadMore,
    onInputChange,
    renderChip,
    loading,
    ...restProps
  } = props

  const error = meta?.touched ? meta.error : undefined
  const actualHelperText = error ?? helperText as string
  const shortName = getShortName(input?.name || name)
  const actualOnChange = input ? input.onChange : onChange
  const actualOnBlur = input ? input.onBlur : onBlur

  // we need to create a copy of value, otherwise it will sometimes get garbled up
  const clonedValue = useMemo(() => value?.slice() || input?.value?.slice() || [], [value || input?.value])

  const allSuggestions = useMemo(() => (
    // If a selected item is not in the suggestions, add it manually, this is needed otherwise the Autocomplete
    // component will complain the value is invalid.
    suggestions.concat(clonedValue.filter(v => !suggestions.find(s => areOptionsEqual(s, v, suggestionsFormat))))
  ), [clonedValue, suggestions, suggestionsFormat])

  const sortedSuggestions = useMemo(
    () => (
      allSuggestions.slice().sort((a, b) => a[suggestionsFormat.text]?.localeCompare(b[suggestionsFormat.text]))
    ),
    [allSuggestions, suggestionsFormat?.text],
  )
  const ref = useRef<HTMLInputElement>(null)

  useImperativeHandle(forwardedRef, () => ref.current)

  const handleChange = useCallback((e: ChangeEvent<unknown>, newValue: T[]) => {
    if (actualOnChange) {
      actualOnChange(newValue || [])
    }
  }, [actualOnChange, suggestionsFormat])

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>, val: string, reason: AutocompleteInputChangeReason) => {
    // workaround to replicate enter key behavior on space key
    // note that a check for endsWith is necessary, as 'value' is not guaranteed to be a string here
    if (e && typeof e.target.value?.endsWith === 'function' && e.target.value?.endsWith(' ') && ref.current) {
      ref.current.dispatchEvent(new KeyboardEvent('keydown', { ...e, keyCode: 13 }))
    } else if (typeof onInputChange === 'function') {
      onInputChange(e, val, reason)
    }
  }, [ref.current])

  const renderCustomTags = useCallback((tagValue: T[], getTagProps: AutocompleteGetTagProps) => {
    return tagValue.map((option, index) => renderChip({
      label: option[suggestionsFormat.text],
      size: 'small',
      ...getTagProps({ index }),
    }, option))
  }, [renderChip, suggestionsFormat?.text])

  const handleScroll = useListBoxScrollListener(loadMore)

  return (
    <Autocomplete
      {...restProps}
      ref={ref}
      className={classes.container}
      multiple
      autoHighlight
      size="small"
      options={sortedSuggestions}
      getOptionLabel={(s: T) => s[suggestionsFormat.text]}
      isOptionEqualToValue={(a: T, b: T) => areOptionsEqual(a, b, suggestionsFormat)}
      value={clonedValue}
      onChange={handleChange}
      onBlur={actualOnBlur}
      onInputChange={handleInputChange}
      noOptionsText={noOptionsText}
      renderTags={renderChip ? renderCustomTags : undefined}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      loading={loading}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          label={label}
          placeholder={placeholder}
          helperText={actualHelperText}
          error={Boolean(error)}
          autoFocus={autoFocus}
          startAdornment={startAdornment}
          endAdornment={endAdornment}
          onKeyDown={(e) => {
            // Prevent form submit being triggered when enter is pressed to confirm an entry
            if (e.keyCode == 13 || e.which == 13) e.preventDefault()
          }}
          data-cy={shortName ? `chipfield-${shortName}` : undefined}
          InputProps={{
            ...params.InputProps,
            inputProps: {
              ...params.inputProps,
              'data-cy': shortName ? `chipfield-input-${shortName}` : undefined,
            },
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  )
}

// Create a properly typed component with generic type parameter
const AutoCompleteChipInputComponent = forwardRef(AutoCompleteChipInput) as <T extends { [key: string]: any }>(
  props: IAutoCompleteChipInputProps<T> & { ref?: ForwardedRef<HTMLInputElement> }
) => JSX.Element

// Export the memoized version while preserving the generic type
export default memo(AutoCompleteChipInputComponent) as typeof AutoCompleteChipInputComponent
